import { useMediaQuery } from '@material-ui/core'
import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import { BaseThemeType, Button, Tooltip } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import useDisclaimerRedirectDialog from 'myenergy/selectionComponent/loanApplicationButton/generic/useDisclaimerRedirectDialog'
import { usePreviewMode } from '../previewMode'
import { logOptimizedCheckoutEvent } from 'myenergy/dialogs/checkout/cashFlow/checkout_2/utils'
import { CheckoutFinancingPricingOption, CheckoutFinancingPricingParams } from '../types'
import MarketplaceCTADialog from './MarketplaceCTADialog'

const REDIRECT_INTEGRATIONS = ['selina']

type PropTypes = {
  pricingOption: CheckoutFinancingPricingOption
  projectId: number
  orgId: number
  systemUuid: string
  paymentOptionId: number
  integration: string
  pricingParams: CheckoutFinancingPricingParams
  setHideDialog: (hide: boolean) => void
  closeRootDialog: () => void
  onApplicationStarted?: () => void
  fromOptimizedCheckout?: boolean
  rank?: number
}

const MarketplaceCTA = (props: PropTypes) => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false)
  const isPro = useSelector(authSelectors.getIsAuthenticatedPro)
  const ctaDisabledForPro = isPro && props.pricingOption.is_pro_facing === false
  const isRedirectIntegration = REDIRECT_INTEGRATIONS.includes(props.integration)
  const previewMode = usePreviewMode()
  const translate = useTranslate()
  const { openDialog: openRedirectDialog, dialogNode: redirectDialogNode } = useDisclaimerRedirectDialog(
    () => {
      // On cancel: reset hideDialog so the finance marketplace dialog is visible again
      props.setHideDialog(false)
    },
    () => {
      // On continue: close both dialogs
      props.setHideDialog(false)
      props.closeRootDialog()
    }
  )

  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))
  const onOpen = () => {
    if (ctaDisabledForPro) return
    props.onApplicationStarted?.()
    props.setHideDialog(true)

    if (isRedirectIntegration && props.pricingOption.cta_dialog_config) {
      openRedirectDialog({
        orgId: props.orgId,
        projectId: props.projectId,
        systemUuid: props.systemUuid,
        paymentOptionId: props.paymentOptionId,
        ctaDialogConfigJson: props.pricingOption.cta_dialog_config,
        integration: props.integration,
        source: 'finance_marketplace',
      })
    } else {
      setDialogIsOpen(true)
    }

    logAmplitudeEventAllowCustomers('marketplace_cta_clicked', {
      org_id: props.orgId,
      project_id: props.projectId,
      integration: props.integration,
      product_title: props.pricingOption.product_description,
      product_type: props.pricingOption.sub_type_display_name || '',
      rank: props.rank,
    })
    if (props.fromOptimizedCheckout) {
      logOptimizedCheckoutEvent('checkout_v2_finance_marketplace_apply_now', {
        lender_name: props.integration,
        loan_offering_name:
          props.pricingOption.product_description || props.pricingOption.sub_type_display_name || '',
      })
    }
  }

  const onClose = () => {
    setDialogIsOpen(false)
    props.setHideDialog(false)
  }

  // The marketplace CTA is framed as "Check Availability" in every context (in-app proposal,
  // standalone shared link, and pro preview) because the next screen shows availability /
  // qualification details before the customer actually submits an application. Using a single
  // label keeps copy consistent between the embedded and standalone experiences.
  const applyButton = (
    <Button
      variant="contained"
      color="secondary"
      size={isMobile ? 'small' : 'large'}
      onClick={onOpen}
      disabled={previewMode || ctaDisabledForPro}
    >
      {translate('Check Availability')}
    </Button>
  )

  return (
    <div>
      {previewMode ? (
        <Tooltip
          title={translate(
            'Preview mode: applications are disabled. Share this link with your customer so they can apply.'
          )}
        >
          <span>{applyButton}</span>
        </Tooltip>
      ) : ctaDisabledForPro ? (
        <Tooltip
          title={translate("Applications for this lender must be submitted from a customer's device")}
        >
          <span>{applyButton}</span>
        </Tooltip>
      ) : (
        applyButton
      )}
      {isRedirectIntegration
        ? redirectDialogNode
        : dialogIsOpen && (
            <MarketplaceCTADialog
              pricingOption={props.pricingOption}
              projectId={props.projectId}
              orgId={props.orgId}
              systemUuid={props.systemUuid}
              paymentOptionId={props.paymentOptionId}
              integration={props.integration}
              open={dialogIsOpen}
              onClose={onClose}
              pricingParams={props.pricingParams}
              setHideDialog={props.setHideDialog}
              closeRootDialog={props.closeRootDialog}
              rank={props.rank}
            />
          )}
    </div>
  )
}

export default MarketplaceCTA
